import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { MatDialogConfig } from '@angular/material/dialog'
import { AlertService } from '@app/alert/service/alert.service'
import { ModalService } from '@app/modal/modal.service'
import { ArquivoModel } from '@shared/models/backend'
import { FileDownloaderService } from '../file-downloader/file-downloader.service'
import { DEFAULT_EXTENSIONS, DEFAULT_MAX_SIZE } from '../file-uploader/file-uploader-default-configs'
import { FileUploaderService } from '../file-uploader/file-uploader.service'

@Component({
  selector: 'app-multiple-file-uploader',
  templateUrl: './multiple-file-uploader.component.html',
  styleUrls: ['./multiple-file-uploader.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultipleFileUploaderComponent implements OnInit {
  @Input()
  title: string

  @Input()
  maxSize = DEFAULT_MAX_SIZE

  @Input()
  accept = DEFAULT_EXTENSIONS

  @Input()
  required = true

  @Input()
  disabled: boolean

  @Input()
  formGroup: FormGroup

  @Input()
  control: FormControl

  @Output()
  removed = new EventEmitter<{
    arquivo: ArquivoModel
    onSuccess: () => void
  }>()

  validExtensions: string[]
  public isUploading = false
  public uploadingFiles: { file: File; isUploading: boolean }[] = []

  constructor(
    private alertService: AlertService,
    private uploaderService: FileUploaderService,
    private changeDetector: ChangeDetectorRef,
    private modalService: ModalService,
    private fileDownloaderService: FileDownloaderService
  ) {}

  ngOnInit(): void {
    this.validExtensions = this.accept.replace(/\s/g, '').split(',')
    this.formGroup.statusChanges.subscribe((_) => this.changeDetector.markForCheck())

    if (!this.control.value) {
      this.control.setValue([])
    }
  }

  get arquivos(): ArquivoModel[] {
    return this.control?.value || []
  }

  private truncate(text, len) {
    if (!text) {
      return text
    }
    const ext = text.substring(text.lastIndexOf('.') + 1, text.length).toLowerCase()
    const filename = text.replace('.' + ext, '')
    if (filename.length <= len) {
      return text
    }
    return filename.substr(0, len).trim() + (text.length > len ? '...' : '') + '.' + ext
  }

  onClick(fileInput: HTMLInputElement) {
    this.formGroup.markAsDirty()
    fileInput.value = ''
    fileInput.click()
  }

  async onChange(event: Event) {
    const files = (event.target as HTMLInputElement).files
    if (files?.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        await this.validateAndAddFile(file)
      }
    }
    this.changeDetector.markForCheck()
  }

  private async validateAndAddFile(file: File) {
    if (file.size > this.maxSize * 1024 * 1024) {
      this.notifyFileSizeProblem(file.name)
      return
    }

    if (!this.validExtensions.some((ext) => file?.name?.toLocaleLowerCase()?.endsWith(ext?.toLocaleLowerCase()))) {
      this.notifyFileExtensionProblem(file.name)
      return
    }

    if (this.arquivos.some((a) => a.nome === file.name)) {
      this.alertService.showError(`O arquivo ${file.name} já foi anexado`, this.title)
      return
    }

    const uploadingFile = { file, isUploading: true }
    this.uploadingFiles.push(uploadingFile)
    this.changeDetector.markForCheck()

    try {
      const arquivo = await this.uploaderService.upload(file).toPromise()

      const currentFiles = this.arquivos || []
      this.control.setValue([...currentFiles, arquivo])

      this.notifyFileUploaded(file.name)
    } catch (err) {
      this.notifyFileUploadProblem(file.name)
    } finally {
      const index = this.uploadingFiles.indexOf(uploadingFile)
      if (index > -1) {
        this.uploadingFiles.splice(index, 1)
      }
      this.changeDetector.markForCheck()
    }
  }

  download(arquivo: ArquivoModel) {
    this.fileDownloaderService.download(arquivo)
  }

  removeFile(arquivo: ArquivoModel) {
    const confirm = this.modalService.openConfirmModal(this.confirmRemoveModalConfig(arquivo.nome))
    confirm.afterClosed().subscribe((result) => {
      if (result) {
        this.removed.emit({
          arquivo: arquivo,
          onSuccess: () => {
            const updatedFiles = this.arquivos.filter((a) => a.id !== arquivo.id)
            this.control.setValue(updatedFiles)

            this.notifyFileRemoved(arquivo.nome)
            this.changeDetector.markForCheck()
          },
        })
      }
    })
  }

  private notifyFileSizeProblem(filename: string) {
    this.alertService.showError(`O arquivo ${filename} excede o tamanho máximo de ${this.maxSize}MB`, this.title)
  }

  private notifyFileExtensionProblem(filename: string) {
    this.alertService.showError(
      `O arquivo ${filename} não tem uma extensão válida. Apenas arquivos ${this.accept} são permitidos`,
      this.title
    )
  }

  private notifyFileUploadProblem(filename: string) {
    this.alertService.showError(`Não foi possível enviar o arquivo ${filename}`, this.title)
  }

  private notifyFileUploaded(filename: string) {
    this.alertService.showSuccess(`Arquivo ${filename} anexado com sucesso`, this.title)
  }

  private notifyFileRemoved(filename: string) {
    this.alertService.showSuccess(`Arquivo ${filename} excluído com sucesso`, this.title)
  }

  private confirmRemoveModalConfig(filename: string) {
    const dialogConfig = new MatDialogConfig()
    dialogConfig.width = '24em'
    dialogConfig.data = {
      title: `Deseja excluir o anexo ${filename}?`,
      primaryOption: 'EXCLUIR',
      secondaryOption: 'CANCELAR',
      message: 'Atenção! Não será possível desfazer esta ação.',
    }

    return dialogConfig
  }
}
